import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { RefreshCw, LogOut, Users, MailCheck, PersonStanding, Send, FileText } from "lucide-react";
import ClassesTab from "@/pages/ClassesTab";
import InvitesTab from "@/pages/InvitesTab";
import SendInvitesModal from "@/pages/SendInvitesModal";
import EmailTemplatesTab from "@/pages/EmailTemplatesTab";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_session-manager-61/artifacts/10bzfs3k_IMG_3016.webp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { key: "child_first_name", label: "First Name",     width: "w-28"  },
  { key: "child_last_name",  label: "Last Name",      width: "w-28"  },
  { key: "date_of_birth",    label: "Date of Birth",  width: "w-28"  },
  { key: "adult_full_name",  label: "Adult Name",     width: "w-36"  },
  { key: "adult_email",      label: "Adult Email",    width: "w-48"  },
  { key: "class_type",       label: "Class Type",     width: "w-40"  },
  { key: "preference",       label: "Preference",     width: "w-32"  },
  { key: "notes",            label: "Notes",          width: "w-40"  },
  { key: "date_joined",      label: "Date Joined",    width: "w-28"  },
];

const WL_SHEETS = ["Current", "Failed Invite"];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("dynamo_token")}` };
}

// ── Skeleton row ──────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr>
      <td className="px-4 py-3 w-10">
        <div className="h-4 w-4 bg-slate-100 rounded animate-pulse" />
      </td>
      <td className="px-4 py-3 w-12">
        <div className="h-4 bg-slate-100 rounded animate-pulse" />
      </td>
      {COLUMNS.map((c) => (
        <td key={c.key} className="px-4 py-3">
          <div className="h-4 bg-slate-100 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [email, setEmail] = useState(localStorage.getItem("dynamo_email") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [activeTab, setActiveTab] = useState("waiting-list");
  const [wlSheet, setWlSheet] = useState("Current");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showSendModal, setShowSendModal] = useState(false);

  const fetchWaitingList = useCallback(async (showToast = false, sheetOverride) => {
    const s = sheetOverride ?? wlSheet;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/waiting-list`, {
        headers: authHeaders(),
        params: { sheet: s },
      });
      setRows(data.data);
      setIsDemo(data.source === "demo");
      setLastRefreshed(new Date());
      if (showToast) {
        toast.success(`Waiting list refreshed — ${data.count} entr${data.count === 1 ? "y" : "ies"}`);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      toast.error("Failed to load waiting list");
    } finally {
      setLoading(false);
    }
  }, [wlSheet]);

  useEffect(() => {
    fetchWaitingList(false);
  }, [fetchWaitingList]);

  function switchWlSheet(s) {
    setWlSheet(s);
    setRows([]);
    setSelectedRows(new Set());
    fetchWaitingList(false, s);
  }

  function logout() {
    localStorage.removeItem("dynamo_token");
    localStorage.removeItem("dynamo_email");
    navigate("/admin", { replace: true });
  }

  const formatTime = (d) =>
    d
      ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      : null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* ── Top nav ── */}
      <nav className="bg-[#1656a4] shadow-md" data-testid="top-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <img
            src={LOGO}
            alt="Dynamo Gymnastics"
            className="h-10 w-auto object-contain"
            style={{ mixBlendMode: 'screen' }}
          />
          <div className="flex items-center gap-4">
            <span
              data-testid="user-email"
              className="text-white/80 text-sm hidden sm:block"
            >
              {email}
            </span>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 text-white/80 hover:text-white
                         text-sm transition-colors"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </nav>

      {/* ── Main content ── */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Demo banner */}
        {isDemo && (
          <div
            data-testid="demo-banner"
            className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3
                       flex items-start gap-3 text-sm"
          >
            <span className="text-blue-500 mt-0.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </span>
            <div>
              <p className="font-medium text-blue-800">Demo data</p>
              <p className="text-blue-600">
                Connect your Google Sheet to see live waiting list data.
              </p>
            </div>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
          <button
            data-testid="tab-waiting-list"
            onClick={() => setActiveTab("waiting-list")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${activeTab === "waiting-list"
                ? "border-[#1656a4] text-[#1656a4]"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <Users size={15} />
            Waiting List
          </button>
          <button
            data-testid="tab-classes"
            onClick={() => setActiveTab("classes")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${activeTab === "classes"
                ? "border-[#1656a4] text-[#1656a4]"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <PersonStanding size={15} />
            Classes
          </button>
          <button
            data-testid="tab-sent-invites"
            onClick={() => setActiveTab("sent-invites")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${activeTab === "sent-invites"
                ? "border-[#1656a4] text-[#1656a4]"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <MailCheck size={15} />
            Invites
          </button>
          <button
            data-testid="tab-email-templates"
            onClick={() => setActiveTab("email-templates")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${activeTab === "email-templates"
                ? "border-[#1656a4] text-[#1656a4]"
                : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <FileText size={15} />
            Email Templates
          </button>
        </div>

        {/* Classes tab */}
        {activeTab === "classes" && <ClassesTab />}

        {/* Invites tab */}
        {activeTab === "sent-invites" && <InvitesTab />}

        {/* Email Templates tab */}
        {activeTab === "email-templates" && <EmailTemplatesTab />}


        {/* Waiting list tab */}
        {activeTab === "waiting-list" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h2
                className="text-base font-semibold text-slate-900"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Waiting List
              </h2>
              {lastRefreshed && (
                <p className="text-xs text-slate-400 mt-0.5">
                  Last refreshed at {formatTime(lastRefreshed)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Sheet sub-tabs */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                {WL_SHEETS.map(s => (
                  <button
                    key={s}
                    data-testid={`wl-sheet-${s.replace(/\s+/g,'-').toLowerCase()}`}
                    onClick={() => switchWlSheet(s)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap
                      ${wlSheet === s
                        ? "bg-white text-[#1656a4] shadow-sm"
                        : "text-slate-500 hover:text-slate-700"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <span
                data-testid="row-count"
                className="text-sm text-slate-500"
              >
                {loading ? "Loading…" : `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`}
              </span>
              {/* Send Invites — appears when rows are selected */}
              {selectedRows.size > 0 && (
                <button
                  data-testid="send-invites-button"
                  onClick={() => setShowSendModal(true)}
                  className="flex items-center gap-1.5 bg-[#ff3131] hover:bg-[#dd1010]
                             text-white text-sm font-medium px-3.5 py-2 rounded-md transition-colors"
                >
                  <Send size={14} />
                  Send Invites ({selectedRows.size})
                </button>
              )}
              <button
                data-testid="refresh-button"
                onClick={() => fetchWaitingList(true)}
                disabled={loading}
                className="flex items-center gap-1.5 bg-[#1656a4] hover:bg-[#124687]
                           text-white text-sm font-medium px-3.5 py-2 rounded-md
                           transition-colors disabled:opacity-60"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-testid="waiting-list-table-wrapper">
            <table className="w-full text-sm" data-testid="waiting-list-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {/* Checkbox column */}
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={rows.length > 0 && selectedRows.size === rows.length}
                      ref={el => { if (el) el.indeterminate = selectedRows.size > 0 && selectedRows.size < rows.length; }}
                      onChange={e => setSelectedRows(e.target.checked ? new Set(rows.map((_, i) => i)) : new Set())}
                      className="w-4 h-4 rounded border-slate-300 text-[#1656a4] cursor-pointer accent-[#1656a4]"
                    />
                  </th>
                  {/* # column */}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap w-12">
                    #
                  </th>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`px-4 py-3 text-left text-xs font-semibold text-slate-500
                                  uppercase tracking-wide whitespace-nowrap ${c.width}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMNS.length + 2}
                      className="px-4 py-12 text-center text-slate-400"
                    >
                      No entries on the waiting list.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={idx}
                      data-testid={`waiting-list-row-${idx}`}
                      className="transition-colors cursor-pointer"
                      style={row.highlight_color
                        ? { backgroundColor: row.highlight_color }
                        : undefined}
                      onMouseEnter={e => {
                        if (!row.highlight_color) e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onMouseLeave={e => {
                        if (!row.highlight_color) e.currentTarget.style.backgroundColor = '';
                        else e.currentTarget.style.backgroundColor = row.highlight_color;
                      }}
                      onClick={() => setSelectedRows(prev => {
                        const next = new Set(prev);
                        next.has(idx) ? next.delete(idx) : next.add(idx);
                        return next;
                      })}
                    >
                      {/* Checkbox cell */}
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`row-checkbox-${idx}`}
                          checked={selectedRows.has(idx)}
                          onChange={() => setSelectedRows(prev => {
                            const next = new Set(prev);
                            next.has(idx) ? next.delete(idx) : next.add(idx);
                            return next;
                          })}
                          className="w-4 h-4 rounded border-slate-300 cursor-pointer accent-[#1656a4]"
                        />
                      </td>
                      {/* # cell */}
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {row.number || <span className="text-slate-300">—</span>}
                      </td>
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          className="px-4 py-3 text-slate-700 whitespace-nowrap"
                        >
                          {row[c.key] || (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </main>

      {/* Send Invites modal */}
      <SendInvitesModal
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        selectedEntries={[...selectedRows].map(idx => ({
          child_first_name: rows[idx]?.child_first_name || "",
          child_last_name:  rows[idx]?.child_last_name  || "",
          adult_email:      rows[idx]?.adult_email      || "",
          adult_full_name:  rows[idx]?.adult_full_name  || "",
          row_index:        idx,
        }))}
        wlSheet={wlSheet}
        onSent={() => {
          setSelectedRows(new Set());
          setShowSendModal(false);
          fetchWaitingList(true);
        }}
      />
    </div>
  );
}
